import { useMutation, useQueryClient } from '@tanstack/react-query';

import { like, unlike } from '@/APIs/likeApi';
import { postContentLog, postContentView } from '@/APIs/viewApi';
import { CONTENT_TYPES, LOG_COLLECTOR_USER_AGENT_DATA } from '@/constants/generalConstants';
import { useParams } from 'next/navigation';
import { secondsToTimestamp } from '@/helpers/timestampConversions';
import { produce } from 'immer';

export default function useUserInteractions() {
    const queryClient = useQueryClient();
    const { id } = useParams();

    const handleFeedsPageLikeEffects = ({ objectId, isRecentlyLiked }) => {
        const cachedCommunityFeed = queryClient.getQueryData(['getCommunityFeed']);
        if (cachedCommunityFeed) {
            queryClient.setQueryData(['getCommunityFeed'], (oldValue) => {
                return produce(oldValue, (draft) => {
                    draft?.pages?.forEach((page) => {
                        page?.results.forEach((post) => {
                            if (post.id === objectId) {
                                post.is_liked = !post.is_liked;
                                post.like_count = isRecentlyLiked
                                    ? post.like_count + 1
                                    : post.like_count - 1;
                            }
                        });
                    });
                });
            });
        }
    };

    const handleCommunityDetailsPageLikeEffects = ({ objectId, isRecentlyLiked }) => {
        const cachedCommunitiesPostsQueryData = queryClient.getQueriesData({
            queryKey: ['getCommunityPosts']
        });
        cachedCommunitiesPostsQueryData.forEach((cachedData) => {
            const queryName = cachedData?.[0]?.[0];
            const queryId = cachedData?.[0]?.[1];

            if (queryName === 'getCommunityPosts' && !!queryId) {
                queryClient.setQueriesData(
                    { queryKey: ['getCommunityPosts', queryId] },
                    (oldValue) => {
                        return produce(oldValue, (draft) => {
                            draft?.pages?.forEach((page) => {
                                page?.results.forEach((post) => {
                                    if (post.id === objectId) {
                                        post.is_liked = !post.is_liked;
                                        post.like_count = isRecentlyLiked
                                            ? post.like_count + 1
                                            : post.like_count - 1;
                                    }
                                });
                            });
                        });
                    }
                );
            }
        });
    };

    const handleRecentUserSubmissionsUserInteractionEffects = ({
        objectId,
        isRecentlyLiked,
        isViewInteraction
    }) => {
        const cachedUgcsQueryData = queryClient.getQueriesData({
            queryKey: ['campaignAllMedia']
        });

        cachedUgcsQueryData.forEach((cachedData) => {
            const queryName = cachedData?.[0]?.[0];
            const queryId = cachedData?.[0]?.[1];

            if (queryName === 'campaignAllMedia' && !!queryId) {
                queryClient.setQueriesData(
                    { queryKey: ['campaignAllMedia', queryId] },
                    (oldValue) => {
                        return produce(oldValue, (draft) => {
                            draft?.pages?.forEach((page) => {
                                page?.results.forEach((post) => {
                                    if (post.id === objectId) {
                                        if (isViewInteraction) {
                                            post.view_count++;
                                        } else {
                                            post.like_count = isRecentlyLiked
                                                ? post.like_count + 1
                                                : post.like_count - 1;
                                        }
                                    }
                                });
                            });
                        });
                    }
                );
            }
        });
    };

    const { mutateAsync: likeEntityMutateAsync, isLoading: likeEntityLoading } = useMutation({
        queryKey: ['likeMutation'],
        mutationFn: like,
        onSuccess: (newData, { objectId }) => {
            handleFeedsPageLikeEffects({ objectId, isRecentlyLiked: true });
            handleCommunityDetailsPageLikeEffects({
                objectId,
                isRecentlyLiked: true
            });
            handleRecentUserSubmissionsUserInteractionEffects({ objectId, isRecentlyLiked: true });
        }
    });

    const { mutateAsync: unlikeEntityMutateAsync, isLoading: unlikeEntityLoading } = useMutation({
        queryKey: ['unlikeMutation'],
        mutationFn: unlike,
        onSuccess: (newData, { objectId }) => {
            handleFeedsPageLikeEffects({ objectId, isRecentlyLiked: false });
            handleCommunityDetailsPageLikeEffects({
                objectId,
                isRecentlyLiked: false
            });
            handleRecentUserSubmissionsUserInteractionEffects({ objectId, isRecentlyLiked: false });
        }
    });

    const { mutateAsync: addViewMutateAsync } = useMutation({
        mutationFn: postContentView,
        onSuccess: (response, { objectId }) => {
            const cachedCommunitiesPostsQueryData = queryClient.getQueriesData({
                queryKey: ['getCommunityPosts']
            });
            cachedCommunitiesPostsQueryData.forEach((cachedData) => {
                const queryName = cachedData?.[0]?.[0];
                const queryId = cachedData?.[0]?.[1];

                if (queryName === 'getCommunityPosts' && !!queryId) {
                    queryClient.setQueriesData(
                        { queryKey: ['getCommunityPosts', queryId] },
                        (oldValue) => {
                            return produce(oldValue, (draft) => {
                                draft?.pages?.forEach((page) => {
                                    page.results.forEach((post) => {
                                        if (post.id === objectId) {
                                            post.view_count++;
                                        }
                                    });
                                });
                            });
                        }
                    );
                }
            });

            const cachedCommunityFeed = queryClient.getQueryData(['getCommunityFeed']);
            if (cachedCommunityFeed) {
                queryClient.setQueryData(['getCommunityFeed'], (oldValue) => {
                    return produce(oldValue, (draft) => {
                        draft?.pages?.forEach((page) => {
                            page.results.forEach((post) => {
                                if (post.id === objectId) {
                                    post.view_count++;
                                }
                            });
                        });
                    });
                });
            }

            handleRecentUserSubmissionsUserInteractionEffects({
                objectId,
                isViewInteraction: true
            });
        }
    });

    const { mutateAsync: logContentMutateAsync } = useMutation({
        mutationFn: postContentLog
    });

    const handleLogContent = async ({
        action,
        objectId,
        sourceId,
        timestamp
    }: {
        action: 'view' | 'skip' | 'use';
        objectId?: number;
        sourceId: string;
        timestamp?: number;
    }) => {
        await logContentMutateAsync({
            objectType: CONTENT_TYPES.ads,
            objectId,
            data: {
                action,
                details: {
                    ...LOG_COLLECTOR_USER_AGENT_DATA,
                    timestamp: timestamp ? secondsToTimestamp(timestamp) : undefined,
                    sourceId
                }
            },
            tags: undefined
        });
    };

    return {
        likeEntityMutateAsync,
        likeEntityLoading,
        unlikeEntityMutateAsync,
        unlikeEntityLoading,
        addViewMutateAsync,
        logContentMutateAsync,
        handleLogContent
    };
}
